import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Users, DollarSign, TrendingDown, Store, X, Pencil, Lock, BadgeCheck, Gift, Trash2, Check, Wallet, ExternalLink, Download, QrCode, CheckCircle2, Circle, Share2, Copy, Image, Newspaper, LayoutDashboard, BarChart3, ArrowRight, Eye, EyeOff } from "lucide-react";
import { BTN_SM, CARD, CHIP, INPUT } from "@/lib/theme";

const BTN_OUTLINE_SM = "inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white text-[#17324D] font-semibold text-sm border border-[#17324D]/20 hover:border-[#17324D] transition-colors disabled:opacity-60";
import { QRCodeCanvas } from "qrcode.react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { CATEGORIES, PERK_SUGGESTIONS } from "@/lib/categories";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const emptyForm = { name: "", description: "", category: "Pizzeria", city: "", address: "", monthly_price: 30, perks: "", weekly_deals: "", winback_discount: 20, voucher_enabled: false, voucher_item: "", active: true };
const emptyReward = { title: "", description: "", reward_type: "free_item", value: "", active: true };

export default function OwnerDashboard() {
  const [clubs, setClubs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [events, setEvents] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [platform, setPlatform] = useState(null);
  const [subscribing, setSubscribing] = useState(false);
  const [connect, setConnect] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [qrDownloaded, setQrDownloaded] = useState(false);
  const [showPromo, setShowPromo] = useState(false);
  const [promoPreview, setPromoPreview] = useState("");
  const [promoHeadline, setPromoHeadline] = useState("");
  const [logoImg, setLogoImg] = useState(null);
  const [logoUrl, setLogoUrl] = useState("");
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [savingBrand, setSavingBrand] = useState(false);
  const [showReward, setShowReward] = useState(false);
  const [editingReward, setEditingReward] = useState(null);
  const [rewardForm, setRewardForm] = useState(emptyReward);
  const [tab, setTab] = useState("overview");

  const loadClubs = () => api.get("/restaurants/mine").then((r) => {
    setClubs(r.data);
    if (r.data.length && !selected) selectClub(r.data[0]);
    return r.data;
  });
  const loadPlatform = () => api.get("/platform/status").then((r) => setPlatform(r.data));
  const loadConnect = () => api.get("/connect/status").then((r) => setConnect(r.data)).catch(() => {});
  useEffect(() => {
    loadPlatform(); loadClubs(); loadConnect();
    const p = new URLSearchParams(window.location.search).get("connect");
    if (p === "done") { toast.success("Stripe account connected!"); window.history.replaceState({}, "", "/owner"); }
  }, []);

  const connectStripe = async () => {
    setConnecting(true);
    try {
      const { data } = await api.post("/connect/onboard", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(apiErr(e)); setConnecting(false); }
  };

  const openStripeDashboard = async () => {
    try {
      const { data } = await api.post("/connect/dashboard");
      window.open(data.url, "_blank");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const subscribePlatform = async () => {
    setSubscribing(true);
    try {
      const { data } = await api.post("/checkout/platform", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(apiErr(e)); setSubscribing(false); }
  };

  const selectClub = async (club) => {
    setSelected(club);
    const [s, m, e, rw, rd] = await Promise.all([
      api.get(`/restaurants/${club.id}/stats`),
      api.get(`/restaurants/${club.id}/members`),
      api.get(`/restaurants/${club.id}/events`),
      api.get(`/rewards/mine`),
      api.get(`/restaurants/${club.id}/redemptions`),
    ]);
    setStats(s.data); setMembers(m.data); setEvents(e.data);
    setRewards(rw.data.filter((x) => x.restaurant_id === club.id));
    setRedemptions(rd.data);
  };

  const reloadRewards = async (club) => {
    const [rw, rd] = await Promise.all([
      api.get(`/rewards/mine`),
      api.get(`/restaurants/${club.id}/redemptions`),
    ]);
    setRewards(rw.data.filter((x) => x.restaurant_id === club.id));
    setRedemptions(rd.data);
  };

  const openNewReward = () => { setEditingReward(null); setRewardForm(emptyReward); setShowReward(true); };
  const openEditReward = (r) => { setEditingReward(r); setRewardForm({ ...r }); setShowReward(true); };
  const setR = (k) => (e) => setRewardForm({ ...rewardForm, [k]: e.target.value });

  const saveReward = async (e) => {
    e.preventDefault();
    const payload = { ...rewardForm, restaurant_id: selected.id, active: true };
    try {
      if (editingReward) { await api.put(`/rewards/${editingReward.id}`, payload); toast.success("Reward updated"); }
      else { await api.post(`/rewards`, payload); toast.success("Reward created"); }
      setShowReward(false);
      await reloadRewards(selected);
    } catch (err) { toast.error(apiErr(err)); }
  };

  const deleteReward = async (id) => {
    try { await api.delete(`/rewards/${id}`); toast.success("Reward deleted"); await reloadRewards(selected); }
    catch (err) { toast.error(apiErr(err)); }
  };

  const fulfillRedemption = async (id) => {
    try { await api.post(`/redemptions/${id}/fulfill`); toast.success("Marked fulfilled"); await reloadRewards(selected); }
    catch (err) { toast.error(apiErr(err)); }
  };

  const toggleActive = async () => {
    if (!selected) return;
    const next = selected.active === false;
    try {
      await api.put(`/restaurants/${selected.id}/active`, { active: next });
      const fresh = await loadClubs();
      const s = (fresh || []).find((x) => x.id === selected.id);
      if (s) setSelected(s);
      toast.success(next ? "Club is live again in Browse Clubs." : "Club hidden from Browse Clubs. Existing members are unaffected.");
    } catch (err) { toast.error(apiErr(err)); }
  };

  const exportMembersCSV = () => {
    const rows = [["Name", "Email", "Phone", "Status", "Renews"]];
    members.forEach((m) => rows.push([
      m.customer_name || "", m.customer_email || "", m.customer_phone || "",
      m.status || "", m.current_period_end ? new Date(m.current_period_end).toLocaleDateString() : "",
    ]));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${(selected?.name || "club").replace(/\s+/g, "-")}-members.csv`;
    a.click();
    toast.success(`Exported ${members.length} member(s) to CSV`);
  };

  const openNew = () => {
    if (!platform?.active) { toast.error("Activate your $40/mo platform subscription first."); return; }
    setEditing(null); setForm(emptyForm); setShowForm(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ ...emptyForm, ...c, perks: (c.perks || []).join("\n"), weekly_deals: (c.weekly_deals || []).join("\n") });
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name, description: form.description,
      category: form.category || "Other",
      city: (form.city || "").trim(),
      address: (form.address || "").trim(),
      monthly_price: parseFloat(form.monthly_price),
      perks: form.perks.split("\n").map((x) => x.trim()).filter(Boolean),
      weekly_deals: form.weekly_deals.split("\n").map((x) => x.trim()).filter(Boolean),
      winback_discount: parseInt(form.winback_discount),
      voucher_enabled: !!form.voucher_enabled,
      voucher_item: (form.voucher_item || "").trim(),
      active: editing ? editing.active !== false : true,
    };
    try {
      if (editing) {
        await api.put(`/restaurants/${editing.id}`, payload);
        toast.success("Club updated");
      } else {
        await api.post("/restaurants", payload);
        toast.success("Club created");
      }
      setShowForm(false);
      const clubsData = await loadClubs();
      const fresh = editing
        ? clubsData.find((c) => c.id === editing.id)
        : (clubsData.find((c) => c.name === payload.name) || clubsData[clubsData.length - 1]);
      if (fresh) await selectClub(fresh);
    } catch (err) { toast.error(apiErr(err)); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const addPerk = (p) => {
    const lines = (form.perks || "").split("\n").map((s) => s.trim()).filter(Boolean);
    if (!lines.includes(p)) setForm({ ...form, perks: [...lines, p].join("\n") });
  };

  const loadLogo = (relUrl) => {
    if (!relUrl) { setLogoImg(null); return; }
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.onload = () => setLogoImg(img);
    img.onerror = () => setLogoImg(null);
    img.src = relUrl.startsWith("http") ? relUrl : `${BACKEND}${relUrl}`;
  };

  useEffect(() => {
    if (showPromo && selected) {
      setPromoHeadline(selected.promo_headline || "");
      setLogoUrl(selected.promo_logo_url || "");
      loadLogo(selected.promo_logo_url || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPromo, selected?.id]);

  const onLogoPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Logo must be under 5MB."); return; }
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/blog/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogoUrl(data.url);
      loadLogo(data.url);
      toast.success("Logo added to your poster.");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setUploadingLogo(false); }
  };

  const removeLogo = () => { setLogoUrl(""); setLogoImg(null); };

  const saveBranding = async () => {
    if (!selected) return;
    setSavingBrand(true);
    try {
      await api.put(`/restaurants/${selected.id}/promo`, { promo_headline: promoHeadline, promo_logo_url: logoUrl });
      const fresh = await loadClubs();
      const s = (fresh || []).find((x) => x.id === selected.id);
      if (s) setSelected(s);
      toast.success("Branding saved — it'll show on every poster.");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setSavingBrand(false); }
  };

  useEffect(() => {
    if (!showPromo || !selected) { setPromoPreview(""); return; }
    const t = setTimeout(() => { const c = buildPoster("poster"); if (c) setPromoPreview(c.toDataURL("image/png")); }, 80);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPromo, selected, promoHeadline, logoImg]);

  const clubJoinUrl = selected ? `${window.location.origin}/clubs/${selected.id}` : "";
  const downloadQR = () => {
    const canvas = document.getElementById("club-join-qr");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `${(selected?.name || "club").replace(/\s+/g, "-")}-join-qr.png`;
    a.click();
    setQrDownloaded(true);
    toast.success("QR downloaded — print it for your tables!");
  };

  const slug = (selected?.name || "club").replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase();

  // Draw a high-resolution, ad-ready poster onto a canvas and return it.
  const buildPoster = (variant) => {
    const src = document.getElementById("promo-qr-src");
    if (!src || !selected) return null;
    const sq = variant === "square";
    const W = 1080;
    const H = sq ? 1080 : 1350;
    const c = document.createElement("canvas");
    c.width = W; c.height = H;
    const ctx = c.getContext("2d");
    ctx.textAlign = "center";
    ctx.fillStyle = "#17324D"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#D94A3D"; ctx.fillRect(0, 0, W, 20);
    ctx.fillStyle = "#D94A3D"; ctx.fillRect(0, H - 20, W, 20);

    let y = sq ? 60 : 78; // running top cursor

    // Optional owner logo (aspect-preserved inside a box)
    if (logoImg && logoImg.width) {
      const box = sq ? 150 : 176;
      const ratio = logoImg.width / logoImg.height;
      let lw = box, lh = box;
      if (ratio > 1) lh = box / ratio; else lw = box * ratio;
      ctx.drawImage(logoImg, (W - lw) / 2, y, lw, lh);
      y += lh + (sq ? 22 : 28);
    } else {
      y += sq ? 30 : 36;
    }

    // Eyebrow / custom headline (auto-shrinks to fit)
    const headline = (promoHeadline || "").trim();
    const eyebrow = headline ? headline.toUpperCase() : "• MEMBERS CLUB •";
    ctx.fillStyle = "#D94A3D";
    let hf = 30;
    ctx.font = `700 ${hf}px Helvetica, Arial, sans-serif`;
    while (ctx.measureText(eyebrow).width > W - 140 && hf > 16) {
      hf -= 2; ctx.font = `700 ${hf}px Helvetica, Arial, sans-serif`;
    }
    y += hf;
    ctx.fillText(eyebrow, W / 2, y);
    y += sq ? 26 : 32;

    // Club name (wrap to max 2 lines)
    ctx.fillStyle = "#FFF4E6";
    const name = (selected.name || "").toUpperCase();
    ctx.font = "700 72px Georgia, 'Times New Roman', serif";
    const words = name.split(" ");
    const lines = []; let line = "";
    words.forEach((w) => {
      const t = line ? line + " " + w : w;
      if (ctx.measureText(t).width > W - 160 && line) { lines.push(line); line = w; }
      else line = t;
    });
    if (line) lines.push(line);
    const nameLines = lines.slice(0, 2);
    nameLines.forEach((l) => { y += 76; ctx.fillText(l, W / 2, y); });
    y += sq ? 22 : 30;

    // Bottom block anchored to the bottom: footer URL, price pill, CTA
    const footerY = H - 54;
    const priceY = footerY - (sq ? 72 : 86);
    const ctaY = priceY - (sq ? 92 : 112);
    const cardBottom = ctaY - (sq ? 90 : 110);

    // White QR card fills the remaining space (centered in the gap)
    const pad = 40;
    let cardSize = Math.min(cardBottom - y, sq ? 500 : 620);
    if (cardSize < 280) cardSize = 280;
    const qrSize = cardSize - pad * 2;
    const cardX = (W - cardSize) / 2;
    const cardY = y + Math.max(0, ((cardBottom - y) - cardSize) / 2);
    const r = 28;
    ctx.fillStyle = "#FFFFFF";
    ctx.beginPath();
    ctx.moveTo(cardX + r, cardY);
    ctx.arcTo(cardX + cardSize, cardY, cardX + cardSize, cardY + cardSize, r);
    ctx.arcTo(cardX + cardSize, cardY + cardSize, cardX, cardY + cardSize, r);
    ctx.arcTo(cardX, cardY + cardSize, cardX, cardY, r);
    ctx.arcTo(cardX, cardY, cardX + cardSize, cardY, r);
    ctx.closePath();
    ctx.fill();
    ctx.drawImage(src, cardX + pad, cardY + pad, qrSize, qrSize);

    // CTA
    ctx.fillStyle = "#FFF4E6";
    ctx.font = "700 58px Georgia, 'Times New Roman', serif";
    ctx.fillText("SCAN TO JOIN", W / 2, ctaY);

    // Price pill
    const price = `$${selected.monthly_price ?? 30}/mo`;
    ctx.font = "700 40px Helvetica, Arial, sans-serif";
    const pw = ctx.measureText(price).width + 72;
    const px = (W - pw) / 2;
    const ph = 72, pr = 36;
    ctx.fillStyle = "#D94A3D";
    ctx.beginPath();
    ctx.moveTo(px + pr, priceY - 50);
    ctx.arcTo(px + pw, priceY - 50, px + pw, priceY - 50 + ph, pr);
    ctx.arcTo(px + pw, priceY - 50 + ph, px, priceY - 50 + ph, pr);
    ctx.arcTo(px, priceY - 50 + ph, px, priceY - 50, pr);
    ctx.arcTo(px, priceY - 50, px + pw, priceY - 50, pr);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = "#FFFFFF";
    ctx.fillText(price, W / 2, priceY);

    // Footer URL
    ctx.fillStyle = "rgba(255,244,230,0.75)";
    ctx.font = "500 26px Helvetica, Arial, sans-serif";
    const host = clubJoinUrl.replace(/^https?:\/\//, "");
    ctx.fillText(host, W / 2, footerY);
    return c;
  };

  const downloadPoster = (variant) => {
    const c = buildPoster(variant);
    if (!c) { toast.error("Could not generate image — try again."); return; }
    const a = document.createElement("a");
    a.href = c.toDataURL("image/png");
    a.download = `${slug}-${variant === "square" ? "social-square" : "poster"}.png`;
    a.click();
    setQrDownloaded(true);
    toast.success(variant === "square" ? "Social square downloaded!" : "Poster downloaded — ready for your ads!");
  };

  const downloadPlainQR = () => {
    const src = document.getElementById("promo-qr-src");
    if (!src) return;
    const a = document.createElement("a");
    a.href = src.toDataURL("image/png");
    a.download = `${slug}-qr-hires.png`;
    a.click();
    setQrDownloaded(true);
    toast.success("High-res QR downloaded!");
  };

  const copyJoinLink = async () => {
    try { await navigator.clipboard.writeText(clubJoinUrl); toast.success("Join link copied!"); }
    catch { toast.error("Copy failed — select and copy manually."); }
  };

  const sharePoster = async () => {
    const c = buildPoster("square");
    const text = `Join ${selected?.name} — become a Regular for $${selected?.monthly_price ?? 30}/mo and unlock member perks.`;
    try {
      if (c && navigator.canShare) {
        const blob = await new Promise((res) => c.toBlob(res, "image/png"));
        const file = new File([blob], `${slug}-share.png`, { type: "image/png" });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: selected?.name, text, url: clubJoinUrl });
          return;
        }
      }
      if (navigator.share) { await navigator.share({ title: selected?.name, text, url: clubJoinUrl }); return; }
      await copyJoinLink();
    } catch { /* user cancelled */ }
  };

  // Revenue projection: guaranteed next month + 6-month growth trend
  const projection = (() => {
    if (!stats) return [];
    const A = stats.active_members || 0;
    const P = stats.monthly_price || 0;
    const rows = [];
    let members = A;
    const labels = ["Now", "+1mo", "+2mo", "+3mo", "+4mo", "+5mo"];
    for (let i = 0; i < labels.length; i++) {
      rows.push({ month: labels[i], revenue: Math.round(members * P), members });
      members = Math.round(members * 1.15) + 2; // modest 15% + 2 new/mo projection
    }
    return rows;
  })();

  const TABS = [
    { id: "overview", label: "Overview", icon: LayoutDashboard },
    { id: "members", label: "Members", icon: Users },
    { id: "perks", label: "Perks & Rewards", icon: Gift },
    { id: "blog", label: "Blog", icon: Newspaper },
    { id: "analytics", label: "Analytics", icon: BarChart3 },
  ];
  const SectionHead = ({ title, sub, children }) => (
    <div className="flex flex-wrap justify-between items-end gap-3 mt-10 mb-4">
      <div>
        <h2 className="font-heading font-bold text-2xl text-[#17324D]">{title}</h2>
        {sub && <p className="text-sm text-[#17324D]/60 mt-0.5">{sub}</p>}
      </div>
      {children && <div className="flex flex-wrap gap-2">{children}</div>}
    </div>
  );
  const Badge = ({ ok, children }) => (
    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full uppercase ${ok ? "bg-[#17324D] text-white" : "bg-[#D94A3D] text-white"}`}>{children}</span>
  );
  const TableWrap = ({ children }) => (
    <div className={`${CARD} overflow-x-auto`}><table className="w-full text-sm">{children}</table></div>
  );
  const TH = ({ children }) => <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-[#17324D]/60">{children}</th>;
  const TD = ({ children, className = "", ...p }) => <td className={`px-4 py-3 ${className}`} {...p}>{children}</td>;

  return (
    <div className="App min-h-screen bg-[#FFF4E6] text-[#17324D]">
      <Navbar />
      <div className="max-w-6xl mx-auto px-5 py-10">
        <div className="flex flex-wrap justify-between items-end gap-4">
          <div>
            <p className="text-xs sm:text-sm font-bold tracking-[0.18em] uppercase text-[#D94A3D]">Owner dashboard</p>
            <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight mt-1 flex items-center gap-3 flex-wrap">
              {selected ? selected.name : "Your Clubs"}
              {selected && selected.active === false && (
                <span data-testid="club-hidden-pill" className="text-[11px] font-bold px-2.5 py-1 rounded-full uppercase bg-[#17324D]/10 text-[#17324D]/70 inline-flex items-center gap-1"><EyeOff size={12} /> Hidden</span>
              )}
            </h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {selected && (
              <>
                <button data-testid="toggle-active-btn" onClick={toggleActive} className={BTN_OUTLINE_SM}
                  title={selected.active === false ? "Show this club in Browse Clubs again" : "Hide this club from Browse Clubs (members keep access)"}>
                  {selected.active === false ? <><Eye size={16} /> Show in Browse</> : <><EyeOff size={16} /> Hide from Browse</>}
                </button>
                <button data-testid="share-promote-btn" onClick={() => setShowPromo(true)} className={BTN_OUTLINE_SM}>
                  <Share2 size={16} /> Share &amp; Promote
                </button>
              </>
            )}
            <button data-testid="new-club-btn" onClick={openNew} className={BTN_SM}>
              <Plus size={16} /> New Club
            </button>
          </div>
        </div>

        {/* Club switcher */}
        {clubs.length > 1 && (
          <div className="flex flex-wrap gap-2 mt-6" data-testid="club-switcher">
            {clubs.map((c) => (
              <button key={c.id} data-testid={`club-tab-${c.id}`} onClick={() => selectClub(c)} className={`${CHIP(selected?.id === c.id)} inline-flex items-center gap-1.5`}>
                {c.name}{c.active === false && <EyeOff size={13} className="opacity-60" />}
              </button>
            ))}
          </div>
        )}
        {clubs.length === 1 && clubs.map((c) => <span key={c.id} data-testid={`club-tab-${c.id}`} className="hidden" />)}

        {/* Section nav */}
        <nav data-testid="owner-section-nav" className="mt-6 flex gap-1 overflow-x-auto border-b border-[#17324D]/10 -mx-5 px-5 sm:mx-0 sm:px-0">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`owner-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === t.id ? "border-[#D94A3D] text-[#D94A3D]" : "border-transparent text-[#17324D]/60 hover:text-[#17324D]"}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </nav>

        {/* ---------- OVERVIEW ---------- */}
        {tab === "overview" && (
          <>
            {selected && selected.active === false && (
              <div data-testid="club-hidden-banner" className={`${CARD} p-4 mt-6 flex flex-wrap items-center justify-between gap-3`}>
                <div className="flex items-center gap-3 text-sm"><EyeOff size={20} className="text-[#17324D]/60" /><span><span className="font-semibold">This club is hidden from Browse Clubs.</span> New diners can't join, but existing members keep their perks and billing.</span></div>
                <button data-testid="banner-show-club-btn" onClick={toggleActive} className={BTN_SM}><Eye size={15} /> Show in Browse</button>
              </div>
            )}
            {platform && (platform.active ? (
              <div data-testid="platform-active-banner" className={`${CARD} p-4 mt-6 flex items-center gap-3`}>
                <BadgeCheck size={22} className="text-[#D94A3D]" />
                <div className="text-sm">
                  <span className="font-semibold">Platform subscription active</span> — ${platform.fee}/mo.
                  Renews {platform.period_end ? new Date(platform.period_end).toLocaleDateString() : "—"}.
                </div>
              </div>
            ) : (
              <div data-testid="platform-gate-banner" className="rounded-2xl p-6 mt-6 bg-[#17324D] text-white">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex gap-3">
                    <span className="bg-white/10 p-2 rounded-lg h-fit"><Lock size={20} /></span>
                    <div>
                      <h3 className="font-heading font-bold text-xl">Activate your club platform</h3>
                      <p className="text-white/70 text-sm">Pay <span className="text-white font-bold">${platform.fee}/month</span> to run your own Regulars Club. Create clubs, take member payments, and track everything.</p>
                    </div>
                  </div>
                  <button data-testid="activate-platform-btn" onClick={subscribePlatform} disabled={subscribing} className={`${BTN_SM} whitespace-nowrap py-3`}>
                    {subscribing ? "Redirecting..." : `Activate for $${platform.fee}/mo`}
                  </button>
                </div>
              </div>
            ))}

            {connect && (connect.connected ? (
              <div data-testid="connect-active-banner" className={`${CARD} p-4 mt-4 flex flex-wrap items-center justify-between gap-3`}>
                <div className="flex items-center gap-3">
                  <Wallet size={22} className="text-[#D94A3D]" />
                  <div className="text-sm"><span className="font-semibold">Stripe account connected</span> — member payments go straight to your bank.</div>
                </div>
                <button data-testid="stripe-dashboard-btn" onClick={openStripeDashboard} className={BTN_OUTLINE_SM}>
                  <ExternalLink size={14} /> Stripe Dashboard
                </button>
              </div>
            ) : (
              <div data-testid="connect-gate-banner" className="rounded-2xl p-6 mt-4 bg-[#17324D] text-white">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex gap-3">
                    <span className="bg-white/10 p-2 rounded-lg h-fit"><Wallet size={20} /></span>
                    <div>
                      <h3 className="font-heading font-bold text-xl">Connect your Stripe account</h3>
                      <p className="text-white/70 text-sm max-w-xl">Link your own Stripe so your members' monthly payments go <b className="text-white">directly to you</b> — the platform never touches your money. Required to take member payments.</p>
                    </div>
                  </div>
                  <button data-testid="connect-stripe-btn" onClick={connectStripe} disabled={connecting} className={`${BTN_SM} whitespace-nowrap py-3`}>
                    {connecting ? "Redirecting..." : "Connect Stripe"}
                  </button>
                </div>
                <div className="mt-5 grid sm:grid-cols-4 gap-3">
                  {[
                    ["1", "Click Connect Stripe", "You'll be taken to Stripe's secure page."],
                    ["2", "Create or log in to Stripe", "No account? Stripe sets one up in minutes — free."],
                    ["3", "Add business & bank details", "So payouts land in your bank automatically."],
                    ["4", "Return here — you're paid!", "Members' $ deposits straight to your account."],
                  ].map(([n, t, d]) => (
                    <div key={n} className="bg-white/5 rounded-xl p-3">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#D94A3D] text-white font-bold text-sm">{n}</span>
                      <p className="font-semibold text-sm mt-2">{t}</p>
                      <p className="text-xs text-white/60 mt-0.5">{d}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {clubs.length === 0 ? (
              <div className={`${CARD} p-10 text-center mt-8`}>
                <span className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-[#D94A3D]/10 text-[#D94A3D]"><Store size={28} /></span>
                <p className="mt-4 font-heading font-bold text-lg">Create your first membership club.</p>
                <button data-testid="empty-new-club-btn" onClick={openNew} className={`${BTN_SM} mt-4`}><Plus size={16} /> New Club</button>
              </div>
            ) : selected && stats && (
              <>
                <div data-testid="quick-start" className={`${CARD} p-6 mt-8`}>
                  <h2 className="font-heading font-bold text-2xl">Quick Start</h2>
                  <p className="text-sm text-[#17324D]/60 mb-5">Three steps to launch <b>{selected.name}</b>.</p>
                  <div className="grid md:grid-cols-3 gap-4">
                    {[
                      { done: !!connect?.connected, title: "Connect Stripe to get paid", sub: "Members pay you directly.",
                        action: !connect?.connected && <button data-testid="qs-connect-btn" onClick={connectStripe} disabled={connecting} className={`${BTN_SM} mt-3 w-full !bg-[#635BFF] hover:!bg-[#5147e5]`}>{connecting ? "..." : "Connect Stripe"}</button> },
                      { done: rewards.length > 0, title: "Set your first reward", sub: "e.g. 10% off any large pie.",
                        action: rewards.length === 0 && <button data-testid="qs-reward-btn" onClick={openNewReward} className={`${BTN_SM} mt-3 w-full`}>Add a Reward</button> },
                      { done: qrDownloaded, title: "Print your \"Join the Club\" QR", sub: null,
                        action: (
                          <div className="flex items-center gap-3 mt-2">
                            <div className="p-1 border border-[#17324D]/15 rounded-lg bg-white"><QRCodeCanvas id="club-join-qr" value={clubJoinUrl} size={56} level="M" /></div>
                            <button data-testid="qs-qr-btn" onClick={downloadQR} className={BTN_OUTLINE_SM}><Download size={14} /> Download</button>
                          </div>
                        ) },
                    ].map((s, i) => (
                      <div key={i} className="rounded-xl bg-[#FFF4E6] p-4">
                        <div className="flex items-center gap-2">
                          {s.done ? <CheckCircle2 size={20} className="text-[#D94A3D]" /> : <Circle size={20} className="text-[#17324D]/40" />}
                          <span className="text-xs font-bold uppercase tracking-wide text-[#17324D]/60">Step {i + 1}</span>
                        </div>
                        <p className="font-semibold mt-2">{s.title}</p>
                        {s.sub && <p className="text-sm text-[#17324D]/60 mt-1">{s.sub}</p>}
                        {s.action}
                      </div>
                    ))}
                  </div>
                </div>

                <StatGrid stats={stats} />

                <SectionHead title="Recent activity" sub="Latest subscription events for this club.">
                  <button data-testid="overview-members-link" onClick={() => setTab("members")} className={BTN_OUTLINE_SM}>View members <ArrowRight size={14} /></button>
                </SectionHead>
                <ActivityFeed events={events.slice(0, 5)} />
              </>
            )}
          </>
        )}

        {/* ---------- MEMBERS ---------- */}
        {tab === "members" && selected && (
          <>
            <SectionHead title="Members" sub={`${members.length} total · ${members.filter((m) => m.status === "active").length} active`}>
              <button data-testid="export-csv-btn" onClick={exportMembersCSV} className={BTN_OUTLINE_SM}><Download size={14} /> Export CSV</button>
            </SectionHead>
            <TableWrap>
              <thead className="bg-[#FFF4E6]"><tr><TH>Name</TH><TH>Email</TH><TH>Phone</TH><TH>Status</TH><TH>Renews</TH><TH>Voucher</TH></tr></thead>
              <tbody>
                {members.length === 0 ? (
                  <tr><td colSpan="6" className="p-8 text-center text-[#17324D]/60">No members yet.</td></tr>
                ) : members.map((m) => (
                  <tr key={m.id} data-testid={`member-row-${m.id}`} className="border-t border-[#17324D]/10">
                    <TD className="font-semibold">{m.customer_name}</TD>
                    <TD>{m.customer_email}</TD>
                    <TD>{m.customer_phone || "—"}</TD>
                    <TD><Badge ok={m.status === "active"}>{m.status}</Badge></TD>
                    <TD>{m.current_period_end ? new Date(m.current_period_end).toLocaleDateString() : "—"}</TD>
                    <TD data-testid={`member-voucher-${m.id}`}>
                      {!m.voucher_enabled ? <span className="text-[#17324D]/40">—</span>
                        : m.voucher_redeemed
                          ? <span className="text-[11px] font-bold px-2 py-0.5 rounded-full uppercase bg-[#17324D] text-white" title={`Redeemed ${m.voucher_last_at ? new Date(m.voucher_last_at).toLocaleString() : ""}`}>Redeemed {m.voucher_last_at ? new Date(m.voucher_last_at).toLocaleDateString() : ""}</span>
                          : <span className="text-[11px] font-bold px-2 py-0.5 rounded-full uppercase bg-[#17324D]/10 text-[#17324D]/70">Not redeemed</span>}
                    </TD>
                  </tr>
                ))}
              </tbody>
            </TableWrap>

            <SectionHead title="Subscription activity" />
            <ActivityFeed events={events.slice(0, 15)} />
          </>
        )}

        {/* ---------- PERKS & REWARDS ---------- */}
        {tab === "perks" && selected && (
          <>
            <SectionHead title="Club perks" sub="What every member gets. Edit the club to change price, perks, voucher and weekly deals.">
              <button data-testid="edit-club-btn" onClick={() => openEdit(selected)} className={BTN_SM}><Pencil size={14} /> Edit Club &amp; Perks</button>
            </SectionHead>
            <div className="grid md:grid-cols-3 gap-4">
              <div className={`${CARD} p-5`}>
                <p className="text-xs font-bold uppercase tracking-wider text-[#17324D]/60">Membership price</p>
                <p className="font-heading font-extrabold text-3xl mt-1">${selected.monthly_price}<span className="text-base font-semibold text-[#17324D]/60">/mo</span></p>
                <p className="text-sm text-[#17324D]/60 mt-1">{selected.category || "Other"}{selected.city ? ` · ${selected.city}` : ""}</p>
              </div>
              <div className={`${CARD} p-5`}>
                <p className="text-xs font-bold uppercase tracking-wider text-[#17324D]/60">Perks</p>
                <ul className="mt-2 space-y-1.5">
                  {(selected.perks || []).length === 0 ? <li className="text-sm text-[#17324D]/60">No perks yet.</li>
                    : (selected.perks || []).map((p, i) => <li key={i} className="text-sm flex gap-2"><Check size={15} strokeWidth={3} className="text-[#D94A3D] mt-0.5 shrink-0" />{p}</li>)}
                </ul>
              </div>
              <div className={`${CARD} p-5`} data-testid="perks-voucher-card">
                <p className="text-xs font-bold uppercase tracking-wider text-[#17324D]/60">Monthly voucher</p>
                {selected.voucher_enabled
                  ? <><p className="font-heading font-bold text-lg mt-1">Free {selected.voucher_item || "item"}</p><p className="text-sm text-[#17324D]/60 mt-1">Once per calendar month, resets on the 1st.</p></>
                  : <p className="text-sm text-[#17324D]/60 mt-2">Off — turn it on in Edit Club to give every member a free item each month.</p>}
                {(selected.weekly_deals || []).length > 0 && (
                  <><p className="text-xs font-bold uppercase tracking-wider text-[#17324D]/60 mt-4">Weekly deals</p>
                  <ul className="mt-1 space-y-1">{selected.weekly_deals.map((d, i) => <li key={i} className="text-sm">{d}</li>)}</ul></>
                )}
              </div>
            </div>

            <SectionHead title="Rewards" sub={`Only rewards for ${selected.name} are shown.`}>
              <button data-testid="new-reward-btn" onClick={openNewReward} className={BTN_SM}><Plus size={15} /> New Reward</button>
            </SectionHead>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {rewards.length === 0 ? <p className="text-[#17324D]/60">No rewards yet.</p> :
                rewards.map((r) => (
                  <div key={r.id} data-testid={`reward-card-${r.id}`} className={`${CARD} p-5`}>
                    <div className="flex justify-between items-start gap-2">
                      <h3 className="font-heading font-bold text-lg leading-tight">{r.title}</h3>
                      <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-[#D94A3D]/10 text-[#D94A3D] whitespace-nowrap">{r.reward_type.replace("_", " ")}</span>
                    </div>
                    <p className="text-sm text-[#17324D]/70 mt-1">{r.description}</p>
                    {r.value && <p className="text-sm mt-1"><span className="text-[#17324D]/60">Value:</span> {r.value}</p>}
                    <div className="mt-4 flex gap-2">
                      <button data-testid={`edit-reward-${r.id}`} onClick={() => openEditReward(r)} className={`${BTN_OUTLINE_SM} px-3 py-1.5 text-xs`}><Pencil size={13} /> Edit</button>
                      <button data-testid={`delete-reward-${r.id}`} onClick={() => deleteReward(r.id)} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg text-[#D94A3D] hover:bg-[#D94A3D]/10 transition-colors"><Trash2 size={13} /> Delete</button>
                    </div>
                  </div>
                ))}
            </div>

            <SectionHead title="Redemptions" sub="Reward codes members have claimed at this club." />
            <TableWrap>
              <thead className="bg-[#FFF4E6]"><tr><TH>Member</TH><TH>Reward</TH><TH>Code</TH><TH>Status</TH><TH>When</TH><TH>{""}</TH></tr></thead>
              <tbody>
                {redemptions.length === 0 ? (
                  <tr><td colSpan="6" className="p-8 text-center text-[#17324D]/60">No redemptions yet.</td></tr>
                ) : redemptions.map((rd) => (
                  <tr key={rd.id} data-testid={`redemption-row-${rd.id}`} className="border-t border-[#17324D]/10">
                    <TD className="font-semibold">{rd.member_name}</TD>
                    <TD>{rd.reward_title}</TD>
                    <TD className="font-mono">{rd.code}</TD>
                    <TD><span className={`text-[11px] font-bold px-2 py-0.5 rounded-full uppercase ${rd.status === "fulfilled" ? "bg-[#17324D] text-white" : "bg-[#D94A3D]/10 text-[#D94A3D]"}`}>{rd.status}</span></TD>
                    <TD>{new Date(rd.created_at).toLocaleDateString()}</TD>
                    <TD>{rd.status === "pending" && <button data-testid={`fulfill-btn-${rd.id}`} onClick={() => fulfillRedemption(rd.id)} className={`${BTN_SM} px-3 py-1 text-xs`}><Check size={13} /> Fulfill</button>}</TD>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          </>
        )}

        {/* ---------- BLOG ---------- */}
        {tab === "blog" && (
          <>
            <SectionHead title="Blog" sub="Write posts for your members — they appear on your public blog page." />
            <Link to="/owner/blog" data-testid="owner-blog-section" className={`${CARD} p-6 flex items-center justify-between gap-4 hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(23,50,77,0.12)] transition-[transform,box-shadow]`}>
              <div className="flex items-center gap-4">
                <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#D94A3D]/10 text-[#D94A3D]"><Newspaper size={24} /></span>
                <div>
                  <p className="font-heading font-bold text-lg">Manage blog posts</p>
                  <p className="text-sm text-[#17324D]/60">Create, edit, publish and moderate comments.</p>
                </div>
              </div>
              <span data-testid="owner-blog-link" className="font-semibold text-[#D94A3D] flex items-center gap-1 whitespace-nowrap">Open <ExternalLink size={16} /></span>
            </Link>
          </>
        )}

        {/* ---------- ANALYTICS ---------- */}
        {tab === "analytics" && selected && stats && (
          <>
            <SectionHead title="Analytics" sub="Revenue, members and churn for this club." />
            <StatGrid stats={stats} className="mt-0" />
            <div data-testid="revenue-projection" className={`${CARD} p-6 mt-6`}>
              <div className="flex flex-wrap justify-between items-start gap-3">
                <div>
                  <p className="text-xs font-bold tracking-[0.18em] uppercase text-[#D94A3D]">Revenue projection</p>
                  <h3 className="font-heading font-bold text-2xl mt-1">Guaranteed next month</h3>
                </div>
                <div className="text-right">
                  <p className="font-heading font-extrabold text-4xl text-[#D94A3D] leading-none">${stats.mrr}</p>
                  <p className="text-sm text-[#17324D]/60">from {stats.active_members} active member{stats.active_members === 1 ? "" : "s"}</p>
                </div>
              </div>
              <div className="h-56 mt-5 min-w-0">
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={220}>
                  <AreaChart data={projection} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                    <defs>
                      <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#D94A3D" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#D94A3D" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(23,50,77,0.1)" vertical={false} />
                    <XAxis dataKey="month" stroke="rgba(23,50,77,0.5)" fontSize={12} tickLine={false} axisLine={false} />
                    <YAxis stroke="rgba(23,50,77,0.5)" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v}`} />
                    <Tooltip contentStyle={{ background: "#17324D", border: "none", borderRadius: 12, color: "#fff" }}
                      formatter={(v, n) => n === "revenue" ? [`$${v}`, "Projected MRR"] : [v, "Members"]} />
                    <Area type="monotone" dataKey="revenue" stroke="#D94A3D" strokeWidth={3} fill="url(#revGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <p className="text-xs text-[#17324D]/50 mt-1">Projected growth trend of active memberships (~15%/mo). Actuals will update as members join.</p>
            </div>
          </>
        )}

        {tab !== "overview" && tab !== "blog" && !selected && (
          <p className="mt-8 text-[#17324D]/60">Create a club first to see this section.</p>
        )}
      </div>

      {/* Club form modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="card-brut-lg rounded-xl bg-[#FFF4E6] w-full max-w-lg max-h-[90vh] overflow-y-auto p-7" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-heading font-bold text-2xl tracking-tight">{editing ? "Edit Club" : "New Club"}</h2>
              <button data-testid="close-form-btn" onClick={() => setShowForm(false)}><X size={22} /></button>
            </div>
            <form onSubmit={save} className="space-y-4">
              <Field label="Club Name"><input data-testid="form-name" required value={form.name} onChange={set("name")} className={inputC} /></Field>
              <Field label="Description"><input data-testid="form-desc" value={form.description} onChange={set("description")} className={inputC} /></Field>
              <Field label="Category">
                <select data-testid="form-category" value={form.category} onChange={set("category")} className={inputC}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="City"><input data-testid="form-city" value={form.city} onChange={set("city")} placeholder="e.g. Philadelphia" className={inputC} /></Field>
              <Field label="Full address (for the map)">
                <input data-testid="form-address" value={form.address} onChange={set("address")} placeholder="123 Main St, Philadelphia, PA 19103" className={inputC} />
                <p className="text-xs text-[#17324D]/50 mt-1">We'll pin your club on the “Find a Club” map so nearby diners can find you. Leave blank to stay off the map.</p>
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Monthly Price ($)"><input data-testid="form-price" type="number" step="0.01" required value={form.monthly_price} onChange={set("monthly_price")} className={inputC} /></Field>
                <Field label="Member Discount (%)"><input data-testid="form-discount" type="number" value={form.winback_discount} onChange={set("winback_discount")} className={inputC} /></Field>
              </div>
              <Field label="Perks (one per line)"><textarea data-testid="form-perks" rows="4" value={form.perks} onChange={set("perks")} placeholder={"1 Free Large Cheese Pizza Every Month\n20% Off Every Order"} className={inputC} />
                <div className="mt-2">
                  <p className="text-xs text-[#17324D]/60 mb-1">Suggested for {form.category}:</p>
                  <div className="flex flex-wrap gap-1.5" data-testid="perk-suggestions">
                    {(PERK_SUGGESTIONS[form.category] || PERK_SUGGESTIONS.Other).map((p) => (
                      <button type="button" key={p} data-testid={`perk-suggest-${p}`} onClick={() => addPerk(p)}
                        className="text-xs px-2 py-1 border border-dashed border-[#17324D]/50 rounded-xl hover:bg-[#FFF4E6] hover:border-[#17324D]/15 transition-colors">
                        + {p}
                      </button>
                    ))}
                  </div>
                </div>
              </Field>

              {/* Monthly Free Item Voucher */}
              <div className="border border-[#17324D]/15 rounded-xl p-4 bg-[#FFF4E6]">
                <label className="flex items-center justify-between cursor-pointer">
                  <span>
                    <span className="font-heading font-bold text-base">Monthly Free Item Voucher</span>
                    <span className="block text-xs text-[#17324D]/60">Give every active member one free item they can redeem once per calendar month (resets on the 1st).</span>
                  </span>
                  <button type="button" data-testid="voucher-toggle" onClick={() => setForm({ ...form, voucher_enabled: !form.voucher_enabled })}
                    className={`relative w-12 h-6 rounded-full border border-[#17324D]/15 transition-colors shrink-0 ${form.voucher_enabled ? "bg-[#17324D]" : "bg-[#17324D]/10"}`}>
                    <span className={`absolute top-0.5 w-4 h-4 bg-white border border-[#17324D]/15 rounded-full transition-all ${form.voucher_enabled ? "left-6" : "left-0.5"}`} />
                  </button>
                </label>
                {form.voucher_enabled && (
                  <div className="mt-3">
                    <label className="block text-xs uppercase text-[#17324D]/70 mb-1 font-semibold">Free item name</label>
                    <input data-testid="voucher-item" value={form.voucher_item} onChange={set("voucher_item")} placeholder="e.g. Pizza, Ice Cream, Steak, Coffee" className={inputC} />
                    <p className="text-xs text-[#17324D]/50 mt-1">Changing the item applies to the next voucher cycle only — vouchers already active keep the current item.</p>
                  </div>
                )}
              </div>

              <Field label="Weekly Deals (one per line)"><textarea data-testid="form-deals" rows="3" value={form.weekly_deals} onChange={set("weekly_deals")} placeholder={"Monday: BOGO slices"} className={inputC} /></Field>

              <button data-testid="save-club-btn" className="w-full py-3 bg-[#D94A3D] text-white font-semibold border border-[#17324D]/15 rounded-xl hover:bg-[#C33E32]">
                {editing ? "Save Changes" : "Create Club"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Reward form modal */}
      {showReward && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={() => setShowReward(false)}>
          <div className="card-brut-lg rounded-xl bg-[#FFF4E6] w-full max-w-lg p-7" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-heading font-bold text-2xl tracking-tight">{editingReward ? "Edit Reward" : "New Reward"}</h2>
              <button data-testid="close-reward-btn" onClick={() => setShowReward(false)}><X size={22} /></button>
            </div>
            <form onSubmit={saveReward} className="space-y-4">
              <Field label="Title"><input data-testid="reward-title" required value={rewardForm.title} onChange={setR("title")} className={inputC} placeholder="Free Large Cheese Pizza" /></Field>
              <Field label="Description"><input data-testid="reward-desc" value={rewardForm.description} onChange={setR("description")} className={inputC} placeholder="One per month for members" /></Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Type">
                  <select data-testid="reward-type" value={rewardForm.reward_type} onChange={setR("reward_type")} className={inputC}>
                    <option value="free_item">Free Item</option>
                    <option value="discount">Discount</option>
                    <option value="other">Other</option>
                  </select>
                </Field>
                <Field label="Value"><input data-testid="reward-value" value={rewardForm.value} onChange={setR("value")} className={inputC} placeholder="e.g. Large Pizza or 20%" /></Field>
              </div>
              <button data-testid="save-reward-btn" className="w-full py-3 bg-[#D94A3D] text-white font-semibold border border-[#17324D]/15 rounded-xl hover:bg-[#C33E32]">
                {editingReward ? "Save Reward" : "Create Reward"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Hidden hi-res QR source used to compose posters */}
      {selected && (
        <div aria-hidden className="fixed left-[-99999px] top-0 opacity-0 pointer-events-none">
          <QRCodeCanvas id="promo-qr-src" value={clubJoinUrl} size={640} level="H" includeMargin fgColor="#17324D" bgColor="#FFFFFF" />
        </div>
      )}

      {/* Share & Promote modal */}
      {showPromo && selected && (
        <div data-testid="promo-modal" className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4" onClick={() => setShowPromo(false)}>
          <div className="card-brut-lg rounded-xl bg-[#FFF4E6] w-full max-w-3xl max-h-[92vh] overflow-y-auto p-7" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-1">
              <div>
                <p className="text-xs tracking-[0.2em] uppercase text-[#D94A3D] font-semibold">Marketing Kit</p>
                <h2 className="font-heading font-bold text-2xl tracking-tight">Share &amp; Promote</h2>
              </div>
              <button data-testid="close-promo-btn" onClick={() => setShowPromo(false)}><X size={22} /></button>
            </div>
            <p className="text-sm text-[#17324D]/60 mb-5">Download an ad-ready image for <b>{selected.name}</b>. Anyone who scans lands on your join page and can become a member in seconds.</p>

            {/* Custom branding: headline + logo */}
            <div className="mb-5 border border-[#17324D]/15 rounded-xl bg-white p-4" data-testid="promo-branding">
              <p className="text-xs tracking-[0.15em] uppercase text-[#17324D] font-bold mb-3">Customize your flyer</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold">Headline</label>
                  <input data-testid="promo-headline-input" value={promoHeadline} maxLength={60}
                    onChange={(e) => setPromoHeadline(e.target.value)}
                    placeholder="MEMBERS CLUB"
                    className="w-full mt-1 px-3 py-2 border border-[#17324D]/15 rounded-xl bg-white focus:outline-none focus:ring-2 ring-[#D94A3D]/40" />
                  <p className="text-xs text-[#17324D]/50 mt-1">Top line of the poster. Leave blank for “MEMBERS CLUB”.</p>
                </div>
                <div>
                  <label className="text-sm font-semibold">Logo</label>
                  <div className="flex items-center gap-3 mt-1">
                    {logoUrl
                      ? <img src={logoUrl.startsWith("http") ? logoUrl : `${BACKEND}${logoUrl}`} alt="logo" data-testid="promo-logo-thumb" className="w-12 h-12 object-contain rounded-xl border border-[#17324D]/15 bg-white" />
                      : <div className="w-12 h-12 rounded-xl border border-dashed border-[#17324D]/20 flex items-center justify-center text-[#17324D]/50"><Image size={18} /></div>}
                    <label data-testid="promo-logo-upload" className="cursor-pointer text-sm font-semibold px-3 py-2 border border-[#17324D]/15 rounded-xl hover:bg-[#FFF4E6]">
                      {uploadingLogo ? "Uploading…" : logoUrl ? "Replace" : "Upload logo"}
                      <input type="file" accept="image/*" className="hidden" onChange={onLogoPick} disabled={uploadingLogo} />
                    </label>
                    {logoUrl && <button data-testid="promo-logo-remove" onClick={removeLogo} className="text-sm text-[#D94A3D] font-semibold underline">Remove</button>}
                  </div>
                  <p className="text-xs text-[#17324D]/50 mt-1">PNG or JPG. Transparent PNG looks best.</p>
                </div>
              </div>
              <button data-testid="save-branding-btn" onClick={saveBranding} disabled={savingBrand}
                className="mt-4 flex items-center gap-2 px-4 py-2 font-semibold bg-[#17324D] text-white border border-[#17324D]/15 rounded-xl hover:bg-[#0F2338] disabled:opacity-60">
                <Check size={16} /> {savingBrand ? "Saving…" : "Save branding"}
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Live preview */}
              <div className="flex items-center justify-center bg-[#17324D] rounded-xl p-4 min-h-[320px]">
                {promoPreview
                  ? <img data-testid="promo-preview" src={promoPreview} alt="Poster preview" className="max-h-[420px] w-auto rounded-xl" />
                  : <div className="text-white/70 text-sm">Generating preview…</div>}
              </div>

              {/* Actions */}
              <div className="flex flex-col gap-3">
                <button data-testid="download-poster-btn" onClick={() => downloadPoster("poster")}
                  className="flex items-center gap-2 px-4 py-3 font-semibold bg-[#D94A3D] text-white border border-[#17324D]/15 rounded-xl hover:bg-[#C33E32]">
                  <Download size={16} /> Download Poster <span className="text-xs opacity-70">(flyers / print · 4:5)</span>
                </button>
                <button data-testid="download-square-btn" onClick={() => downloadPoster("square")}
                  className="flex items-center gap-2 px-4 py-3 font-semibold border border-[#17324D]/15 rounded-xl hover:bg-[#FFF4E6]">
                  <Image size={16} /> Download Social Square <span className="text-xs opacity-60">(Instagram · 1:1)</span>
                </button>
                <button data-testid="download-qr-btn" onClick={downloadPlainQR}
                  className="flex items-center gap-2 px-4 py-3 font-semibold border border-[#17324D]/15 rounded-xl hover:bg-[#FFF4E6]">
                  <QrCode size={16} /> Download QR only <span className="text-xs opacity-60">(hi-res PNG)</span>
                </button>
                <div className="border-t border-dashed border-[#17324D]/10 my-1" />
                <button data-testid="share-poster-btn" onClick={sharePoster}
                  className="flex items-center gap-2 px-4 py-3 font-semibold bg-[#17324D] text-white border border-[#17324D]/15 rounded-xl hover:bg-[#0F2338]">
                  <Share2 size={16} /> Share…
                </button>
                <div className="flex items-center gap-2">
                  <input data-testid="join-link-input" readOnly value={clubJoinUrl}
                    className="flex-1 min-w-0 text-sm border border-[#17324D]/15 rounded-xl px-3 py-2 bg-white font-mono truncate" />
                  <button data-testid="copy-link-btn" onClick={copyJoinLink}
                    className="flex items-center gap-1 px-3 py-2 text-sm font-semibold border border-[#17324D]/15 rounded-xl hover:bg-[#FFF4E6] shrink-0">
                    <Copy size={14} /> Copy
                  </button>
                </div>
                <p className="text-xs text-[#17324D]/50">Tip: put the poster on table tents, receipts, your window, and social posts. The QR never changes, so print once and reuse.</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inputC = INPUT;
const Field = ({ label, children }) => (
  <div><label className="text-sm font-semibold">{label}</label>{children}</div>
);

const StatGrid = ({ stats, className = "mt-6" }) => (
  <div className={`grid grid-cols-2 lg:grid-cols-4 gap-4 ${className}`}>
    {[
      { icon: Users, label: "Active Members", value: stats.active_members },
      { icon: DollarSign, label: "Monthly Revenue", value: `$${stats.mrr}` },
      { icon: Wallet, label: "Total Collected", value: `$${stats.total_revenue}` },
      { icon: TrendingDown, label: "Churn Rate", value: `${stats.churn_rate}%` },
    ].map((s, i) => (
      <div key={i} data-testid={`stat-${i}`} className={`${CARD} p-5`}>
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-[#D94A3D]/10 text-[#D94A3D]"><s.icon size={18} /></span>
        <p className="text-xs font-bold uppercase tracking-wider text-[#17324D]/60 mt-3">{s.label}</p>
        <p className="font-heading font-extrabold text-2xl sm:text-3xl mt-0.5">{s.value}</p>
      </div>
    ))}
  </div>
);

const ActivityFeed = ({ events }) => (
  <div className={`${CARD} divide-y divide-[#17324D]/10`}>
    {events.length === 0 ? <p className="p-6 text-sm text-[#17324D]/60">No activity yet.</p> :
      events.map((ev) => (
        <div key={ev.id} className="px-5 py-3 flex flex-wrap justify-between items-center gap-2">
          <span className="text-sm"><span className="font-bold text-[#D94A3D] uppercase text-[11px] mr-2">{ev.type.replace("_", " ")}</span>{ev.message}</span>
          <span className="text-xs text-[#17324D]/50">{new Date(ev.created_at).toLocaleString()}</span>
        </div>
      ))}
  </div>
);
